const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

/**
 * Uses Gemini TTS model to speak the text. Falls back to browser TTS if API fails.
 */
export async function textToSpeech(
  text: string,
  callbacks: { onStart?: () => void; onEnd?: () => void } = {}
) {
  const { onStart, onEnd } = callbacks;
  const cleanText = text.replace(/[*#`]/g, "");

  try {
    if (!API_KEY) throw new Error("No API Key");

    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-tts:generateContent?key=${API_KEY}`;

    const payload = {
      contents: [
        {
          parts: [{ text: `Say in German: ${cleanText}` }]
        }
      ],
      generationConfig: {
        response_modalities: ["AUDIO"],
        speech_config: {
          voice_config: {
            prebuilt_voice_config: {
              voice_name: "Kore"
            }
          }
        }
      }
    };

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(
        `Gemini TTS API Error: ${errData?.error?.message || response.statusText}`
      );
    }

    const data = await response.json();
    const base64Audio =
      data.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;

    if (!base64Audio) throw new Error("No audio data received from Gemini");

    const pcmBytes = Uint8Array.from(atob(base64Audio), (c) => c.charCodeAt(0));
    const wavBytes = pcmToWav(pcmBytes, 24000, 1, 16);

    const audioBlob = new Blob([wavBytes], { type: "audio/wav" });
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);

    if (onStart) onStart();

    audio.onended = () => {
      if (onEnd) onEnd();
      URL.revokeObjectURL(audioUrl);
    };

    audio.onerror = (e) => {
      console.error("Audio playback error", e);
      if (onEnd) onEnd();
    };

    await audio.play();
  } catch (error) {
    console.warn("Gemini TTS failed, falling back to browser TTS:", error);
    fallbackToBrowserTTS(cleanText, callbacks);
  }
}

function fallbackToBrowserTTS(
  text: string,
  { onStart, onEnd }: { onStart?: () => void; onEnd?: () => void }
) {
  if (!("speechSynthesis" in window)) {
    if (onEnd) onEnd();
    return;
  }

  window.speechSynthesis.cancel();
  if (onStart) onStart();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "de-DE";

  const voices = window.speechSynthesis.getVoices();
  const germanVoice =
    voices.find((v) => v.name.includes("Google Deutsch")) ||
    voices.find((v) => v.lang.includes("de"));

  if (germanVoice) utterance.voice = germanVoice;
  utterance.pitch = 1.0;
  utterance.rate = 0.9;

  utterance.onend = () => {
    if (onEnd) onEnd();
  };

  utterance.onerror = () => {
    if (onEnd) onEnd();
  };

  window.speechSynthesis.speak(utterance);
}

export function pcmToWav(
  pcmData: Uint8Array,
  sampleRate = 24000,
  numChannels = 1,
  bitDepth = 16
) {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);

  const byteRate = (sampleRate * numChannels * bitDepth) / 8;
  const blockAlign = (numChannels * bitDepth) / 8;
  const dataSize = pcmData.length;
  const fileSize = 36 + dataSize;

  writeString(view, 0, "RIFF");
  view.setUint32(4, fileSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  const wavFile = new Uint8Array(header.byteLength + pcmData.length);
  wavFile.set(new Uint8Array(header), 0);
  wavFile.set(pcmData, 44);
  return wavFile;
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

export class AudioQueue {
  private queue: string[] = [];
  private isPlaying = false;
  private callbacks: any;
  private runId = 0;

  constructor(callbacks = {}) {
    this.callbacks = callbacks;
  }

  add(text: string) {
    this.queue.push(text);
    if (!this.isPlaying) {
      this.playNext(this.runId);
    }
  }

  async playNext(currentRunId: number) {
    if (currentRunId !== this.runId) {
      this.isPlaying = false;
      return;
    }

    if (this.queue.length === 0) {
      this.isPlaying = false;
      if (this.callbacks.onEnd) this.callbacks.onEnd();
      return;
    }

    if (!this.isPlaying) {
      this.isPlaying = true;
      if (this.callbacks.onStart) this.callbacks.onStart();
    }

    const text = this.queue.shift()!;

    await new Promise<void>((resolve) => {
      if (currentRunId !== this.runId) {
        resolve();
        return;
      }
      textToSpeech(text, {
        onStart: () => {},
        onEnd: () => resolve()
      });
    });

    this.playNext(currentRunId);
  }

  stop() {
    this.runId++;
    this.queue = [];
    this.isPlaying = false;
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (this.callbacks.onEnd) this.callbacks.onEnd();
  }
}
